<template>
  <Dialog :open="showModal" @update:open="onDialogUpdate">
    <DialogScrollContent>
      <form method="post" @submit.prevent="submitUpdate">
        <DialogHeader>
          <DialogTitle>
            {{ orderItem ? $t('Edit activity') : $t('Add activity') }}
          </DialogTitle>
        </DialogHeader>

        <div v-if="state === 'initializing'">
          {{ $t('loading…') }}
        </div>

        <fieldset v-else :disabled="state === 'updating'">
          <div class="form-group">
            <label class="control-label">{{ $t('Activity') }}</label>
            <select
              v-model="selectedActivity"
              class="form-control"
              name="activitySelected"
              id="activitySelected"
            >
              <option
                v-for="activity in activities"
                :value="activity.id"
              >{{ activity.code}}</option>
            </select>
          </div>
          <div v-if="activitySelected">
            <hr>
            <h4>{{ activitySelected.name }}</h4>
            <VariantSelector :activity="activitySelected"
                             v-model="variant"
                             @change="parentOptions = $event"
                             class="mb-3"
            >
            </VariantSelector>
            <template v-if="variant">
              <VariantInformation :activity="activitySelected"
                                  :variant="variant"
                                  :order-item-price="orderItemPrice"
                                  :order-item-initial-price="orderItemInitialPrice ? orderItemInitialPrice : activitySelected.price"
                                  :update-price="activityHasChanged"
                                  v-on:price-change="orderItemPrice = $event"
              ></VariantInformation>
            </template>
            <Alert v-else variant="info">
              {{ $t('No variant selected') }}
            </Alert>
            <Alert v-if="orderItem && activitySelected.number_of_participants > orderItem.participants.length" variant="info">
              {{ $t("You don’t have enough participants for this activity, you need to add:") }}
              {{ $t('number of participant', {nbr: activitySelected.number_of_participants - orderItem.participants.length }, activitySelected.number_of_participants - orderItem.participants.length) }}
            </Alert>
            <Alert v-if="orderItem && activitySelected.number_of_participants < orderItem.participants.length" variant="info">
              {{ $t("You have too many participants for this activity, you need to remove:") }}
              {{ $t('number of participant', {nbr: orderItem.participants.length - activitySelected.number_of_participants }, orderItem.participants.length - activitySelected.number_of_participants) }}
            </Alert>
            <div v-if="associatedActivities.length > 0" class="mt-3">
              <div v-for="(associatedActivity, i) in associatedActivities" :key="i">
                <AssociatedActivities :associated-activity="associatedActivity"
                                       :parent-options="parentOptions"
                                       @update-variant="updateAssociatedActivityVariant(associatedActivity, $event)"
                                       class="mb-2"
                ></AssociatedActivities>
              </div>
            </div>
          </div>
        </fieldset>
        <Alert v-if="updateErrors.length > 0" variant="danger">
          <span v-for="updateError in updateErrors">
            {{ updateError[0] }}
          </span>
        </Alert>

        <DialogFooter>
          <button
            class="btn btn-primary"
            :disabled="state === 'updating'"
          >{{ orderItem ? $t('Save') : $t('Add to order') }}</button>
        </DialogFooter>
      </form>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import VariantSelector from "@/components/orders/VariantSelector.vue";
import AssociatedActivities from "@/components/orders/AssociatedActivities.vue";
import VariantInformation from "@/components/orders/VariantInformation.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Alert from "@/components/ui/Alert.vue";

const props = defineProps({
  orderItem: {
    type: Object,
    default: () => {}
  },
  children : {
    type: Array,
    default: () => { return []}
  },
  updateItemUrl : {
    required: true,
    type: String,
  },
  showModal : {
    required: true,
    type: Boolean,
  },
  activitiesUrl : {
    required: true,
    type: String,
  },
})

const orderItemActivityVariantId = ref(props.orderItem ? props.orderItem?.variant.id : null);
const variant = ref({});
const orderItemPrice = ref(props.orderItem ? (props.orderItem.discounted_price ? props.orderItem.discounted_price : props.orderItem.price) : null);
const orderItemInitialPrice = ref(props.orderItem ? ( props.orderItem.discounted_price ? props.orderItem.discounted_price : props.orderItem.price) : null);
const parentOptions = ref({});
const associatedActivities = ref([]);
const state = ref('initializing');
const activities = ref({});
const selectedActivity = ref(0); // null ?
const activityHasChanged = ref(false);
const orderItemActivityId = ref(props.orderItem ? props.orderItem.activity.id : null);
const updateErrors = ref([]);

const getCatalog = async () => {
  try {
    state.value = 'initializing';
    const { data } = await axios.get(props.activitiesUrl)
    state.value = 'idle';
    activities.value = data.data
    selectedActivity.value = orderItemActivityId.value
  } catch (e) {
    alert(e.message)
  }
}

watch(
  () => props.showModal,
  (val) => {
    if (val) {
      getCatalog()
    }
})

const activitySelected = computed(() => {
  if (activities.value.length === 0 || state.value === 'initializing') {
    return null
  }
  return activities.value.find(activity => parseInt(activity.id) === parseInt(selectedActivity.value))
})

watch(
  () => selectedActivity.value,
  (val) => {
    if (orderItemActivityId.value !== val) {
      activityHasChanged.value = true
    }

    if (activitySelected.value) {
      variant.value = activitySelected.value.variants.find(variant => parseInt(variant.id) === parseInt(orderItemActivityVariantId.value))
      if (!orderItemPrice.value) {
        orderItemPrice.value = activitySelected.value.price
      }
    }

    if (! activitySelected.value || !Array.isArray(activitySelected.value.associated_activities)) {
      associatedActivities.value =  []
    } else {
      associatedActivities.value = activitySelected.value.associated_activities.map((activity) => {
        const child = props.children.find(child => parseInt(child.activity.id) === parseInt(activity.id))
        let variant = null
        if (child) {
          variant = activity.variants.find(variant => parseInt(variant.id) === parseInt(child.variant.id))
        }
        // console.log('variant', activity, variant)
        return {
          activity,
          item :  child ? child.uuid : null,
          variant : variant ?? null,
          orderItemPrice: child ? ( child.discounted_price ? child.discounted_price : child.price) : null,
        }
      })
    }
  }
)

const updateAssociatedActivityVariant = (associatedActivity, variant) =>{
  // console.log('updateAssociatedActivityVariant', associatedActivity, variant)
  let index = associatedActivities.value.findIndex(function(item) {
    return parseInt(item.activity.id) === parseInt(associatedActivity.activity.id)
  })
  // console.log('index', index)
  if (index > -1) {
    associatedActivities.value.splice(index, 1, variant)
  } else {
    associatedActivities.value.push(variant)
  }
}

const formData = computed(() => {
  if (!variant.value) {
    return null
  }
  return  {
    activity: variant.value.id,
    activityPrice: orderItemPrice.value.amount,
    associated_activities : associatedActivities.value
      .map(function(associatedActivity) {
        return {
          orderItem: associatedActivity.item,
          activity : associatedActivity.variant ? associatedActivity.variant.id : null,
          activityPrice: associatedActivity.orderItemPrice?.amount,
        };
      })
  }
})

const submitUpdate = async () => {
  state.value = 'updating';
  updateErrors.value = [];
  try {
    const {data} = await axios.put(props.updateItemUrl, {
      ...formData.value,
      _token : window.Laravel.csrfToken,
    })
    location.reload();
  } catch (error) {
    updateErrors.value = Object.values(error.response.data.message);
    state.value = 'idle';
  }
}

const emits = defineEmits([
  'exit-modal',
]);
const onDialogUpdate = (isOpen) => {
  if (!isOpen) {
    emits('exit-modal')
  }
}
</script>
